import { CSSProperties, useState } from 'react';
import {
  TRACKING_ICON_SLOTS,
  TrackingIconAnchor,
  TrackingIconSlot,
} from '../models/TrackingIcon';
import styles from './TrackingIconMapPreview.module.css';

interface TrackingIconMapPreviewProps {
  slot: TrackingIconSlot;
  imageUrl?: string | null;
  name: string;
  anchor: TrackingIconAnchor;
  canChangeImage?: boolean;
}

const statusStyle: CSSProperties = {
  position: 'absolute',
  zIndex: 4,
  bottom: 12,
  left: 12,
  right: 12,
  margin: 0,
  padding: '8px 12px',
  borderRadius: 8,
  background: 'rgba(255, 255, 255, 0.95)',
  color: '#475467',
  fontSize: 13,
  textAlign: 'center',
};

const PreviewImage = ({
  imageUrl,
  name,
  anchor,
  canChangeImage,
}: Omit<TrackingIconMapPreviewProps, 'imageUrl' | 'slot'> & {
  imageUrl: string;
}) => {
  const [imageState, setImageState] = useState<'ready' | 'error'>();
  const offset =
    anchor === 'top' ? '0%' : anchor === 'center' ? '-50%' : '-100%';

  return (
    <>
      <img
        data-testid='preview-icon'
        src={imageUrl}
        alt={name || 'Ícono seleccionado'}
        onLoad={() => setImageState('ready')}
        onError={() => setImageState('error')}
        style={{
          position: 'absolute',
          zIndex: 2,
          left: '50%',
          top: '50%',
          transform: `translate(-50%, ${offset})`,
          display: 'block',
          width: 'auto',
          height: 'auto',
          maxWidth: 'none',
          maxHeight: 'none',
          visibility: imageState === 'ready' ? 'visible' : 'hidden',
        }}
      />
      {imageState !== 'ready' && (
        <p role='status' style={statusStyle}>
          {imageState === 'error'
            ? canChangeImage
              ? 'No se pudo mostrar la imagen. Elegí otro archivo para ver su posición.'
              : 'No se pudo cargar la imagen de este ícono.'
            : 'Cargando vista previa…'}
        </p>
      )}
    </>
  );
};

const TrackingIconMapPreview = ({
  slot,
  imageUrl,
  name,
  anchor,
  canChangeImage = true,
}: TrackingIconMapPreviewProps) => (
  <figure style={{ margin: '4px 0 0', minWidth: 0 }}>
    <figcaption
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: 8,
      }}
    >
      <span style={{ fontSize: 14, fontWeight: 600, color: '#344054' }}>
        Vista previa en el mapa
      </span>
      <span style={{ fontSize: 12, color: '#667085' }}>Mapa de ejemplo</span>
    </figcaption>
    <div
      data-testid='preview-map'
      style={{
        position: 'relative',
        height: 240,
        overflow: 'hidden',
        borderRadius: 12,
        border: '1px solid #e4e7ec',
        background: '#eef1ed',
      }}
    >
      <svg
        aria-hidden='true'
        focusable='false'
        viewBox='0 0 640 240'
        preserveAspectRatio='xMidYMid slice'
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <rect width='640' height='240' fill='#eef1ed' />
        <rect x='30' y='22' width='132' height='74' rx='14' fill='#dbe7d6' />
        <rect x='358' y='152' width='118' height='64' rx='12' fill='#e1e7df' />
        <path
          d='M548 -20 C498 54 602 104 554 156 S534 232 582 270'
          fill='none'
          stroke='#d5e5e8'
          strokeWidth='46'
        />
        <g fill='none' stroke='#dce1db' strokeWidth='22'>
          <path d='M0 120 H640 M320 0 V240 M190 0 V240 M496 0 V240 M0 28 H640 M0 220 H640' />
        </g>
        <g fill='none' stroke='#fff' strokeWidth='18'>
          <path d='M0 120 H640 M320 0 V240 M190 0 V240 M496 0 V240 M0 28 H640 M0 220 H640' />
        </g>
      </svg>
      <div
        data-testid='preview-axis-x'
        aria-hidden='true'
        className={styles.axisX}
      />
      <div
        data-testid='preview-axis-y'
        aria-hidden='true'
        className={styles.axisY}
      />
      {imageUrl ? (
        <PreviewImage
          key={imageUrl}
          imageUrl={imageUrl}
          name={name}
          anchor={anchor}
          canChangeImage={canChangeImage}
        />
      ) : (
        <p role='status' style={statusStyle}>
          {canChangeImage
            ? 'Elegí una imagen para ver cómo se ubica sobre el punto.'
            : 'Este ícono no tiene una imagen disponible.'}
        </p>
      )}
      {slot === TRACKING_ICON_SLOTS.driver && (
        <span
          data-testid='preview-coordinate'
          aria-label='Centro de ubicación'
          role='img'
          className={styles.coordinate}
        >
          <span
            data-testid='preview-wave'
            aria-hidden='true'
            className={styles.wave}
          />
        </span>
      )}
    </div>
    <p
      style={{
        margin: '8px 0 0',
        fontSize: 12,
        lineHeight: 1.5,
        color: '#667085',
      }}
    >
      El cruce de los ejes marca la ubicación. La imagen se muestra a tamaño
      original.
    </p>
  </figure>
);

export default TrackingIconMapPreview;
